import { Component, InjectionToken, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RS_POPOVER_CONTEXT } from './rs-popover.service';

export interface IndexPickerState {
  selectedIndexes: WritableSignal<string[]>;
  availableIndexes: Signal<{ name: string; count: number }[]>;
}
export const INDEX_PICKER_STATE = new InjectionToken<IndexPickerState>('IndexPickerState');

/**
 * Index picker — searchable multi-select flyout shared by the index-based
 * rate sheets. Hosted in a CDK overlay by RsPopoverService, anchored to
 * the rail's "Indexes" trigger.
 */
@Component({
  selector: 'app-index-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="ip">
      <div class="ip-h">
        <span>{{ ctx.title }}</span>
        <span class="ip-x" (click)="ctx.close()">✕</span>
      </div>
      <div class="ip-search">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input [ngModel]="query()" (ngModelChange)="query.set($event)" placeholder="Search indexes…" autofocus>
      </div>
      <div class="ip-list">
        @for (i of filtered(); track i.name) {
          <label class="ip-row" [class.sel]="isOn(i.name)">
            <input type="checkbox" [checked]="isOn(i.name)" (change)="toggle(i.name)">
            <span class="ip-nm">{{ i.name }}</span>
            <span class="ip-ct">{{ i.count }}</span>
          </label>
        }
        @if (filtered().length === 0) { <div class="ip-empty">No indexes match.</div> }
      </div>
      <div class="ip-foot">
        <span class="ip-cnt">{{ state.selectedIndexes().length || 'All' }}{{ state.selectedIndexes().length ? ' selected' : ' indexes' }}</span>
        <span class="ip-clear" (click)="clear()">Clear → all</span>
      </div>
    </div>
  `,
  styles: [`
    .ip { width: 300px; background: #fff; border: 1px solid var(--border2, #dce3eb); border-radius: 8px; box-shadow: var(--shadow-lg, 0 12px 32px rgba(13,42,69,.18)); overflow: hidden; animation: ip-in .16s ease-out; }
    @keyframes ip-in { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
    .ip-h { display: flex; align-items: center; padding: 10px 12px; border-bottom: 1px solid var(--border-light); font-weight: 700; color: var(--navy); font-size: 13px; }
    .ip-h .ip-x { margin-left: auto; color: var(--muted); cursor: pointer; font-size: 15px; }
    .ip-search { display: flex; align-items: center; gap: 6px; margin: 8px 12px; border: 1px solid var(--border); border-radius: 6px; padding: 5px 8px; color: var(--muted); }
    .ip-search input { border: none; outline: none; flex: 1; font-family: var(--sans); font-size: 12px; color: var(--text); }
    .ip-list { max-height: 300px; overflow-y: auto; padding-bottom: 4px; }
    .ip-row { display: flex; align-items: center; gap: 9px; padding: 6px 12px; cursor: pointer; }
    .ip-row:hover { background: var(--blue-10); }
    .ip-row.sel { background: #f3f7fc; }
    .ip-row input { margin: 0; flex-shrink: 0; }
    .ip-nm { font-size: 12px; color: var(--text); flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .ip-ct { font-size: 10px; color: var(--muted); font-family: var(--mono); flex-shrink: 0; }
    .ip-empty { padding: 14px 12px; color: var(--muted); font-size: 12px; }
    .ip-foot { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid var(--border-light); padding: 8px 12px; }
    .ip-cnt { font-size: 11px; color: var(--text2); font-weight: 600; }
    .ip-clear { font-size: 11px; color: var(--blue); font-weight: 600; cursor: pointer; }
  `],
})
export class IndexPickerComponent {
  protected readonly ctx = inject(RS_POPOVER_CONTEXT);
  protected readonly state = inject(INDEX_PICKER_STATE);

  protected readonly query = signal('');

  protected readonly filtered = computed(() => {
    const q = this.query().toLowerCase().trim();
    const sel = new Set(this.state.selectedIndexes());
    const all = this.state.availableIndexes()
      .filter(i => !q || i.name.toLowerCase().includes(q));
    return [...all].sort((a, b) => Number(sel.has(b.name)) - Number(sel.has(a.name)));
  });

  isOn(name: string): boolean { return this.state.selectedIndexes().includes(name); }
  toggle(name: string): void {
    const cur = this.state.selectedIndexes();
    this.state.selectedIndexes.set(cur.includes(name) ? cur.filter(n => n !== name) : [...cur, name]);
  }
  clear(): void { this.state.selectedIndexes.set([]); }
}
